import { useState } from 'react';
import { ChatConfigProvider, FilePreviewModal, type ChatPreviewType } from '@odo/shared-ui';
import api from '../../../api/config';
import { fetchPurchaseOrderFileUrl } from '../../../services/purchaseOrderFiles';
import { useToast } from '../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../utils/apiError';
import type { PurchaseOrderFileRef } from '../../../services/purchaseOrders';
import { previewTypeFor } from './poFilePreview';

// FilePreviewModal reads its PDF-proxy target from chat config context - the
// zone has no chat surface of its own, so this supplies just enough of it.
const PREVIEW_CHAT_CONFIG = {
    apiBaseUrl: api.defaults.baseURL ?? '',
    getAuthToken: () => localStorage.getItem('odo_login_token')
};

interface PurchaseOrderInvoiceLinkProps {
    purchaseOrderId: string;
    invoiceNumber: string | null;
    file: PurchaseOrderFileRef | null;
    mimeType: string | null;
}

// The invoice number IS the affordance: a stored file opens it (in-app for
// image/PDF, a new tab otherwise); no file leaves it as plain text.
export function PurchaseOrderInvoiceLink({
    purchaseOrderId,
    invoiceNumber,
    file,
    mimeType
}: PurchaseOrderInvoiceLinkProps) {
    const { showToast } = useToast();
    const [isOpening, setIsOpening] = useState(false);
    const [preview, setPreview] = useState<{ type: ChatPreviewType; url: string } | null>(null);

    if (!invoiceNumber) return <span className="text-gray-400">none recorded</span>;
    if (!file) return <span>{invoiceNumber}</span>;

    const previewType = previewTypeFor(mimeType ?? file.mimeType);

    const open = async () => {
        setIsOpening(true);
        try {
            const { url } = await fetchPurchaseOrderFileUrl(purchaseOrderId, file.id);
            if (previewType) {
                setPreview({ type: previewType, url });
            } else {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to open the invoice'), 'error');
        } finally {
            setIsOpening(false);
        }
    };

    return (
        <>
            <button
                type="button"
                disabled={isOpening}
                onClick={() => {
                    void open();
                }}
                className="underline decoration-gray-300 underline-offset-2 hover:text-gray-900 disabled:opacity-50"
            >
                {isOpening ? 'Opening…' : invoiceNumber}
            </button>
            {preview && (
                <ChatConfigProvider config={PREVIEW_CHAT_CONFIG}>
                    <FilePreviewModal
                        type={preview.type}
                        url={preview.url}
                        name={file.originalFilename}
                        onClose={() => setPreview(null)}
                    />
                </ChatConfigProvider>
            )}
        </>
    );
}
